package br.com.vvrefeicoes.portal.domain.model

import kotlinx.serialization.json.JsonObject

/** Tipo de campo do formulário "Novo RMP" (mesmo nome do enum da API; desconhecido vira [UNKNOWN] e não é desenhado). */
enum class RmpFieldKind {
    SECTION,
    TEXT,
    TEXTAREA,
    NUMBER,
    DATE,
    EMAIL,
    SELECT,
    RADIO,
    CHECKBOX,
    CURRENCY,
    MONTH_PERIOD,
    FILE,
    MULTI_FILE,
    UNKNOWN,
    ;

    val isFile: Boolean get() = this == FILE || this == MULTI_FILE
}

/** Origem das opções de unidade de um seletor: as unidades do usuário, todas (por id) ou todas (por nome). */
enum class RmpOptionSource { LINKED_UNITS, ALL_UNITS, UNIT_NAMES }

data class RmpFormOption(
    val value: String,
    val label: String,
)

/** "O campo [field] vale [value]": usado para mostrar ou exigir outro campo. */
data class RmpFieldCondition(
    val field: String,
    val value: String,
)

data class RmpFormField(
    val name: String?,
    val label: String,
    val kind: RmpFieldKind,
    val required: Boolean = false,
    val options: List<RmpFormOption> = emptyList(),
    val source: RmpOptionSource? = null,
    val showWhen: RmpFieldCondition? = null,
    val requiredWhen: RmpFieldCondition? = null,
    val placeholder: String? = null,
    val note: String? = null,
    val maxLength: Int? = null,
    val allowZero: Boolean = false,
    val readOnly: Boolean = false,
    val defaultValue: String? = null,
)

data class RmpFormType(
    val value: String,
    val label: String,
    val fields: List<RmpFormField>,
)

/** Tudo que a tela precisa: tipos com campos e as unidades de cada origem. */
data class RmpFormSchema(
    val types: List<RmpFormType>,
    val linkedUnits: List<RmpPortalOption>,
    val allUnits: List<RmpPortalOption>,
)

data class RmpRequestAttachment(
    val id: String,
    val field: String,
    val fileName: String,
    val sizeBytes: Long,
)

/** Formulário salvo; [data] tem os valores com as chaves do esquema (inclusive unidade e centro de custo). */
data class RmpRequestDetail(
    val id: String,
    val number: Long?,
    val type: String,
    val typeLabel: String?,
    val status: String,
    val companyName: String?,
    val data: JsonObject,
    val attachments: List<RmpRequestAttachment>,
)

/** Linha de "Meus formulários". */
data class RmpRequestSummary(
    val id: String,
    val number: Long?,
    val type: String,
    val typeLabel: String?,
    val status: String,
    val companyName: String?,
    val employeeName: String?,
    val createdAt: String?,
    val submittedAt: String?,
)

/** Campo preenchido do RMP, já formatado pela API; [section] marca um título de seção (sem valor). */
data class RmpFieldValue(
    val label: String,
    val value: String?,
    val section: Boolean,
)

/** Uma decisão do fluxo: etapa, "APROVADO" ou "REJEITADO", observação ou motivo, quem e quando. */
data class RmpApprovalEntry(
    val stageLabel: String?,
    val decision: String,
    val note: String?,
    val decidedByName: String?,
    val decidedAt: String?,
    /** Identifica a decisão (o Admin escolhe quais remover ao reabrir o RMP). */
    val id: String? = null,
)

/** RMP do portal para quem acompanha ou aprova: campos, etapa atual, se o usuário pode decidir e o histórico. */
data class RmpApproval(
    val request: RmpRequestDetail,
    val fields: List<RmpFieldValue>,
    val requesterName: String?,
    val waitingStageLabel: String?,
    val waitingName: String?,
    val canDecide: Boolean,
    val approvals: List<RmpApprovalEntry>,
    /** O usuário pode alterar os campos e anexos do RMP enviado. */
    val canEdit: Boolean = false,
    /** O usuário pode excluir o RMP. */
    val canDelete: Boolean = false,
    /** Obrigatórios vazios (ex.: anexo removido): a etapa só é aprovada depois de preenchidos. */
    val missingFields: List<String> = emptyList(),
    /** Checklist da transferência; null nos outros tipos. */
    val transferChecklist: RmpTransferChecklist? = null,
    /** Avaliação de desempenho da mudança de cargo; null nos outros tipos. */
    val evaluation: RmpEvaluation? = null,
    /** O usuário pode preencher a avaliação (gestor da unidade no organograma). */
    val canEvaluate: Boolean = false,
    /** Candidatos convocados da admissão com teste prático; null nos outros RMPs. */
    val testAttempts: List<RmpTestAttempt>? = null,
    /** O usuário pode convocar candidatos (RH da unidade). */
    val canConvoke: Boolean = false,
    /** O usuário pode escolher o candidato do teste (gestora da unidade). */
    val canRecordTestResult: Boolean = false,
    /** Situação do RH depois da conclusão ("Apto") ou null. */
    val hrStatus: String? = null,
    /** O usuário pode reabrir o RMP removendo decisões (RMP_ADMIN). */
    val canReopen: Boolean = false,
    /** O usuário pode substituir o candidato da admissão concluída. */
    val canReplaceCandidate: Boolean = false,
    /** O usuário pode marcar o RMP concluído como "Apto". */
    val canMarkApt: Boolean = false,
)

/** Unidade do portal e as regras do RMP marcadas para ela (ex.: a admissão dispensa o CEO VV). */
data class RmpUnitRule(
    val companyId: String,
    val companyName: String,
    val admissionWaivesCeo: Boolean,
)

/** Item do checklist da transferência: valor "OK", "N/A" ou null (ainda não marcado). */
data class RmpTransferChecklistItem(
    val key: String,
    val label: String,
    val value: String?,
)

/** Checklist da transferência: itens, observação, quem preencheu e se está completo (com ASO e assinatura). */
data class RmpTransferChecklist(
    val items: List<RmpTransferChecklistItem>,
    val observation: String?,
    val filledByName: String?,
    val filledAt: String?,
    val complete: Boolean,
)

/** Critério da avaliação de desempenho: nota de 0 a 10 (null enquanto vazia). */
data class RmpEvaluationCriterion(
    val key: String,
    val group: String,
    val label: String,
    val score: Int?,
)

/**
 * Avaliação de desempenho da mudança de cargo: [data] traz os campos de texto (nome, data_admissao, cargo, setor,
 * periodo_avaliacao, habilidades_desenvolver, acoes_executar, parecer_avaliacao), já sugeridos pelo RMP se vazia.
 */
data class RmpEvaluation(
    val done: Boolean,
    val data: Map<String, String>,
    val criteria: List<RmpEvaluationCriterion>,
    val average: Double?,
    val evaluatorName: String?,
    val updatedAt: String?,
)

/**
 * Candidato convocado para o teste prático: [data] com os campos da convocação (conv_nome_candidato, conv_cpf,
 * data_convocacao...) e [result] "pendente", "aprovado" (o escolhido pela gestora) ou "reprovado".
 */
data class RmpTestAttempt(
    val id: String,
    val data: Map<String, String>,
    val result: String,
    val createdByName: String?,
    val createdAt: String?,
)
